import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Cpu, Tag, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { BACKEND_URL } from '../../config/api';

const API_URL = BACKEND_URL;

const LatestFilesPreview = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLatestFiles();
  }, []);

  const fetchLatestFiles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/latest-files?page=1&page_size=6`);
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files || []);
      }
    } catch (error) {
      console.error('Error fetching latest files:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no files
  if (!loading && files.length === 0) {
    return null;
  }

  return (
    <section className="py-20 px-6 bg-[#050505] relative">
      <div className="absolute inset-0 grid-bg opacity-10" />
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-purple-500/30 mb-4">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-purple-400">Just Added</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight uppercase text-white">
            Latest <span className="text-purple-400">ECU Files</span>
          </h2>
          <p className="text-[#888] mt-4 max-w-2xl mx-auto">
            Fresh ECU calibration files ready for instant download. Original and professionally modified versions available.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Files Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {files.slice(0, 6).map((file) => (
                <Link
                  key={file.id}
                  to={`/latest-files/${file.id}`}
                  className={`group bg-[#0A0A0A] border p-5 hover:border-purple-500/50 transition-all duration-300 relative ${
                    file.is_featured ? 'border-[#FFD700]/50' : 'border-white/10'
                  }`}
                  data-testid={`latest-file-${file.id}`}
                >
                  {/* Featured Badge */}
                  {file.is_featured && (
                    <div className="absolute -top-2.5 left-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                      ⭐ Featured
                    </div>
                  )}
                  
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-purple-400 font-mono text-sm mb-1 truncate">
                        {file.calibration_no}
                      </div>
                      <div className="text-white font-semibold text-sm group-hover:text-purple-400 transition truncate">
                        {file.vehicle_details}
                      </div>
                    </div>
                    {file.has_dcu && (
                      <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 ml-2 flex-shrink-0">
                        +DCU
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="text-[#666] text-xs mb-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-3 h-3" />
                      <span className="truncate">{file.ecu_type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag className="w-3 h-3" />
                      <span>{file.modification_type}</span>
                    </div>
                    {/* DCU Details */}
                    {file.has_dcu && file.dcu_calibration_no && (
                      <div className="text-purple-400/70 pt-1 border-t border-white/5">
                        <span className="font-mono">{file.dcu_calibration_no}</span>
                        {file.dcu_ecu_type && <span> • {file.dcu_ecu_type}</span>}
                      </div>
                    )}
                  </div>

                  {/* Prices */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="text-[10px] text-[#666] uppercase">Original</div>
                        <div className="text-green-400 font-bold text-sm">${file.original_price}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-[#666] uppercase">Modified</div>
                        <div className="text-purple-400 font-bold text-sm">${file.modified_price}</div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#666] group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>

            {/* View All Button */}
            <div className="text-center">
              <Link
                to="/latest-files"
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
                data-testid="view-all-latest-files-btn"
              >
                <FileText className="w-5 h-5" />
                View All Latest Files
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default LatestFilesPreview;
